import { ErrorModal } from "@dexteel/mesf-core";
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import type {
	CheckboxSelectionCallbackParams,
	ColDef,
	GetRowIdParams,
	GridApi,
	GridReadyEvent,
	ICellRendererParams,
	IRowNode,
	ITooltipParams,
	RowClassParams,
	RowStyle,
	SelectionChangedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useState } from "react";
import { AgGridTooltip } from "@/components/ag-grid-tooltip";
import type { GeneratableDownDayPM } from "../../repositorie/TaskListRepositorie";
import { useGeneratableDownDayPMs } from "../../hooks/useTaskListQueries";
import { useCreateDownDayPMOrders } from "../../hooks/useTaskListMutations";
import { getCraftLetter } from "@/utils/craftLetter";

// Colored craft badge with the craft initial, matching the Task List grids.
const renderCraftCell = (props: ICellRendererParams) => {
	if (!props?.data) return null;
	return (
		<div
			style={{
				height: 32,
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
			}}
		>
			<div
				style={{
					height: 26,
					width: 26,
					borderRadius: 4,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					background: props.data?.CraftColor || "#FFFFFF",
				}}
			>
				<div style={{ color: "#FFF", fontWeight: 600 }}>
					<div>{getCraftLetter(props.data?.CraftName, props.data?.CraftCode)}</div>
				</div>
			</div>
		</div>
	);
};

const SCHEDULE_LABELS: Record<string, string> = {
	I: "Immediate",
	C: "After last close",
	O: "After last open",
	M: "Monthly",
	P: "Custom",
};

const scheduleLabel = (row?: GeneratableDownDayPM) => {
	if (!row?.SchedulingPolicyCode) return "";
	if (row.SchedulingPolicyCode === "D")
		return row.DownDayCode === "N" ? "Next down day" : "Down day";
	return SCHEDULE_LABELS[row.SchedulingPolicyCode] ?? row.SchedulingPolicyCode;
};

type Props = {
	show: boolean;
	assetId: number | string | null;
	// 'D' = Down day, 'N' = Next down day. The list the generated orders land in.
	downDayCode: string | null;
	onHide: (shouldRefresh: boolean) => void;
};

// Intermediate picker for the "Generate (Next) Down Day PMs" actions: lists every active
// template in the area, whatever its schedule, and opens an order for the selected ones in
// the list given by downDayCode. Templates with an open work order are shown disabled with
// that order's name, since a template keeps one open order at a time.
export const DownDayPMsPicker = ({
	show,
	assetId,
	downDayCode,
	onHide,
}: Props) => {
	const title =
		downDayCode === "N" ? "ADD NEXT DOWN DAY ORDERS" : "ADD DOWN DAY ORDERS";
	const [filterInput, setFilterInput] = useState("");
	const [selectedIds, setSelectedIds] = useState<number[]>([]);
	const [error, setError] = useState("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);

	const { rows, isLoading, error: loadError } = useGeneratableDownDayPMs(
		assetId ?? null,
		show,
	);
	const { createDownDayPMOrders, isCreating } = useCreateDownDayPMOrders({
		onSuccess: () => onHide(true),
		onError: (e) => setError(e.message),
	});

	useEffect(() => {
		if (show) {
			setFilterInput("");
			setSelectedIds([]);
		}
	}, [show]);

	useEffect(() => {
		if (loadError) setError(loadError.message);
	}, [loadError]);

	useEffect(() => {
		gridApi?.setGridOption("quickFilterText", filterInput.trim());
	}, [filterInput, gridApi]);

	const columns = useMemo<ColDef[]>(
		() => [
			{
				headerName: "",
				flex: 1,
				maxWidth: 33,
				headerCheckboxSelection: true,
				// Hide the checkbox on templates that already have an open work order.
				checkboxSelection: (params: CheckboxSelectionCallbackParams) =>
					!params.data?.HasOpenOrder,
			},
			{
				field: "Title",
				headerName: "PM",
				flex: 2,
			},
			{
				field: "AssetName",
				headerName: "Asset",
				flex: 2,
			},
			{
				colId: "Schedule",
				headerName: "Schedule",
				flex: 1.5,
				valueGetter: (params) => scheduleLabel(params.data),
			},
			{
				field: "OpenWorkOrderName",
				headerName: "Open order",
				minWidth: 105,
				maxWidth: 115,
			},
			{
				field: "CraftName",
				headerName: "Craft",
				minWidth: 70,
				maxWidth: 80,
				cellRenderer: renderCraftCell,
				sortable: false,
			},
		],
		[],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			// MUI-styled tooltip, only on templates that already have an open work order.
			tooltipComponent: "agGridTooltip",
			tooltipValueGetter: (params: ITooltipParams) =>
				params.data?.HasOpenOrder
					? `${params.data.OpenWorkOrderName} is still open for this template. Close it to generate a new one.`
					: null,
		}),
		[],
	);

	// Templates that already have an open work order are read-only: not selectable
	// and shown greyed out at the bottom of the list.
	const isRowSelectable = (node: IRowNode<GeneratableDownDayPM>) =>
		!node?.data?.HasOpenOrder;

	const getRowStyle = (params: RowClassParams): RowStyle | undefined =>
		params.data?.HasOpenOrder
			? { backgroundColor: "#f5f5f5", color: "#6b7280" }
			: undefined;

	const onGridReady = (event: GridReadyEvent) => setGridApi(event.api);

	const onSelectionChanged = (event: SelectionChangedEvent) => {
		const ids = event.api
			.getSelectedRows()
			.map((r: GeneratableDownDayPM) => r.TemplateId);
		setSelectedIds(ids);
	};

	const handleCreate = () => {
		createDownDayPMOrders({
			assetId: assetId ?? null,
			templateIds: selectedIds,
			downDayCode,
		});
	};

	return (
		<>
			<Dialog open={show} onClose={() => onHide(false)} maxWidth="md" fullWidth>
				<DialogTitle style={{ padding: "24px 24px 0" }}>
					<Typography variant="h5" style={{ margin: 0, fontWeight: 600 }}>
						{title}
					</Typography>
				</DialogTitle>
				<DialogContent>
					<Grid2 container style={{ padding: "0 0 10px" }}>
						<Grid2 size={{ xs: 12 }}>
							<TextField
								label="Search"
								variant="outlined"
								value={filterInput}
								onChange={(e) => setFilterInput(e.target.value)}
								margin="dense"
								fullWidth
							/>
						</Grid2>
					</Grid2>
					<div style={{ width: "100%", height: "50vh" }}>
						<AgGridReact
							getRowId={(params: GetRowIdParams) =>
								String(params.data.TemplateId)
							}
							rowData={rows}
							columnDefs={columns}
							defaultColDef={defaultColDef}
							components={{ agGridTooltip: AgGridTooltip }}
							rowHeight={32}
							headerHeight={32}
							animateRows={true}
							onGridReady={onGridReady}
							onSelectionChanged={onSelectionChanged}
							rowSelection="multiple"
							rowMultiSelectWithClick={true}
							isRowSelectable={isRowSelectable}
							getRowStyle={getRowStyle}
							tooltipShowDelay={400}
							suppressContextMenu={true}
							suppressCellFocus={true}
							loading={isLoading}
							overlayNoRowsTemplate="No PM templates in this area"
						/>
					</div>
				</DialogContent>
				<DialogActions style={{ padding: "16px 24px 24px", gap: 8 }}>
					<Button
						color="inherit"
						variant="contained"
						onClick={() => onHide(false)}
					>
						CANCEL
					</Button>
					<Button
						color="primary"
						variant="contained"
						onClick={handleCreate}
						disabled={isCreating || selectedIds.length === 0}
						startIcon={
							isCreating ? (
								<CircularProgress size="1rem" color="inherit" />
							) : undefined
						}
					>
						CREATE
					</Button>
				</DialogActions>
			</Dialog>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
